import { Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { IGlobalState } from '~reducer';
import { currencyFormatter } from '~utilities/formatters';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';
import ILoan from '~Api/Loan/ILoan';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import { warehousePendingDrawdownsSelector } from '~Warehouses/selectors';
import { warehousePendingDrawdownsListAction } from '~Warehouses/actions';
import LoanDrawdownStatusEnum from '~Api/Loan/LoanDrawdownStatusEnum';
import LoanDrawdownStatusTag from '~Loans/Loan/LoanDrawdownStatusTag';

export default function PendingDrawdowns(): ReactElement {
    const { warehouseUuid } = useParams<{ warehouseUuid: string }>();

    const pendingDrawdowns: IDictionary<ILoanDrawdown> = useSelector((state: IGlobalState) => warehousePendingDrawdownsSelector(state, warehouseUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!pendingDrawdowns) {
            dispatch(warehousePendingDrawdownsListAction(warehouseUuid));
        }
    }, [
        dispatch,
        pendingDrawdowns,
        warehouseUuid,
    ]);

    if (!pendingDrawdowns) {
        return (
            <Layout uuid={warehouseUuid} section='pending-drawdowns'>
                <Typography.Title level={2}>Pending Drawdowns</Typography.Title>
                <Spin/>
            </Layout>
        );
    }

    const columns: ColumnsType<ILoanDrawdown> = [
        {
            dataIndex: 'loan',
            render: (loan: ILoan) => <Link to={`/loans/${loan.uuid}/drawdowns`}>{loan.code}</Link>,
            title: 'Loan',
            width: '10%',
        },
        {
            dataIndex: 'loan',
            defaultSortOrder: 'ascend',
            render: (loan: ILoan) => <Link to={`/loans/${loan.uuid}/drawdowns`}>{loan.name}</Link>,
            sorter: (a: ILoanDrawdown, b: ILoanDrawdown) => (a.loan.name || '').localeCompare(b.loan.name || ''),
            title: 'Name',
        },
        {
            dataIndex: 'drawdownNumber',
            render: (drawdownNumber: string, loanDrawdown: ILoanDrawdown) => <Link to={`/loans/${loanDrawdown.loanUuid}/drawdowns/${loanDrawdown.uuid}`}>{drawdownNumber}</Link>,
            title: 'Number',
            width: '6%',
        },
        {
            dataIndex: 'requestedAmount',
            render: (requestedAmount: number) => currencyFormatter.format(requestedAmount),
            title: 'Requested Amount',
            width: '15%',
        },
        {
            dataIndex: 'approvedDrawdownAmount',
            render: (approvedDrawdownAmount: number) => approvedDrawdownAmount ? currencyFormatter.format(approvedDrawdownAmount) : '-',
            title: 'Approved Amount',
            width: '15%',
        },
        {
            render: (loanDrawdown: ILoanDrawdown) => <LoanDrawdownStatusTag loanDrawdown={loanDrawdown} />,
            title: 'Status',
            width: '15%',
        },
    ];

    const summary: (data: readonly ILoanDrawdown[]) => ReactNode = (data: readonly ILoanDrawdown[]) => {
        let totalRequestedAmount: number = 0;
        let totalApprovedAmount: number = 0;

        _.each(data, (drawdown: ILoanDrawdown) => {
            totalRequestedAmount += drawdown.requestedAmount;
            totalApprovedAmount += drawdown.approvedDrawdownAmount;
        });

        return (
            <Table.Summary fixed={true}>
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>&nbsp;</Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>&nbsp;</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{currencyFormatter.format(totalRequestedAmount)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{currencyFormatter.format(totalApprovedAmount)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>&nbsp;</Table.Summary.Cell>
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    return (
        <Layout uuid={warehouseUuid} section='pending-drawdowns'>
            <Typography.Title level={2}>Pending Drawdowns</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(pendingDrawdowns)}
                pagination={false}
                rowKey='uuid'
                size='middle'
                summary={summary}
            />
        </Layout>
    );
}
